/**
 * Base renderer class with common functionality
 * Uses offscreen canvas for efficient rendering (like map tiles)
 * Requirements: 6.1-6.5
 */

import type { SimulationState, SimulationConfig, Position } from '@langton/shared';
import type { IRenderer, RenderOptions } from '@langton/shared';
import { getTileTransform } from './tiled-renderer.js';
import { loadAvatar } from './avatar-renderer.js';

// Avatar image cache
const avatarCache = new Map<string, HTMLImageElement>();

/**
 * Abstract base class for all grid renderers
 * Uses offscreen canvas approach for performance:
 * 1. Grid is rendered once to offscreen canvas
 * 2. Only changed cells are updated
 * 3. Display just copies/transforms the offscreen canvas
 */
/**
 * Default cell size for offscreen canvas rendering.
 * Higher values = better quality but more memory.
 * Can be adjusted via setOffscreenCellSize() method.
 */
const DEFAULT_OFFSCREEN_CELL_SIZE = 50;

export abstract class BaseRenderer implements IRenderer {
  protected avatarImage: HTMLImageElement | null = null;
  
  // Offscreen canvas for the grid (rendered once, updated incrementally)
  private offscreenCanvas: HTMLCanvasElement | null = null;
  private offscreenCtx: CanvasRenderingContext2D | null = null;
  private offscreenCellSize = DEFAULT_OFFSCREEN_CELL_SIZE;
  private lastRenderedState: { grid: number[][]; stepCount: number } | null = null;
  private configId: string | null = null;

  /**
   * Set the cell size for offscreen canvas rendering.
   * Higher values = better quality but more memory usage.
   * Recommended range: 10-50
   * @param size - Cell size in pixels (default: 20)
   */
  setOffscreenCellSize(size: number): void {
    if (size !== this.offscreenCellSize) {
      this.offscreenCellSize = Math.max(5, Math.min(100, size));
      this.invalidateCache();
    }
  }

  /**
   * Get current offscreen cell size
   */
  getOffscreenCellSize(): number {
    return this.offscreenCellSize;
  }

  /**
   * Get the number of directions for this grid type
   */
  abstract getDirectionCount(): number;

  /**
   * Get the angle in radians for a given direction
   */
  abstract getDirectionAngle(direction: number): number;

  /**
   * Calculate cell position in canvas coordinates
   */
  abstract getCellPosition(x: number, y: number, cellSize: number): Position;

  /**
   * Draw a single cell at the given grid position
   */
  abstract drawCell(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    cellSize: number
  ): void;

  /**
   * Get the clipping path for avatar (for non-square grids)
   * Returns null for square grids (no clipping needed)
   */
  abstract getAvatarClipPath(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    size: number
  ): Path2D | null;

  /**
   * Calculate the actual pixel dimensions needed for the offscreen canvas
   * Override in subclasses for non-square grids (hex, triangular)
   */
  getOffscreenDimensions(gridWidth: number, gridHeight: number, cellSize: number): { width: number; height: number } {
    return {
      width: gridWidth * cellSize,
      height: gridHeight * cellSize
    };
  }

  /**
   * Get tile dimensions for tiling (may differ from offscreen dimensions due to geometry)
   * Override in subclasses for non-square grids to adjust tile spacing
   * @param gridWidth - Grid width in cells
   * @param gridHeight - Grid height in cells
   * @param cellSize - Cell size in pixels
   * @param cellGapX - Horizontal gap adjustment (positive = larger tiles, negative = smaller)
   * @param cellGapY - Vertical gap adjustment (positive = larger tiles, negative = smaller)
   */
  getTileDimensions(gridWidth: number, gridHeight: number, cellSize: number, cellGapX: number = 0, cellGapY: number = 0): { width: number; height: number } {
    const dims = this.getOffscreenDimensions(gridWidth, gridHeight, cellSize);
    return {
      width: dims.width + cellGapX,
      height: dims.height + cellGapY
    };
  }

  /**
   * Load avatar image for a config (with caching)
   */
  async loadAvatarImage(avatarUrl: string): Promise<void> {
    if (avatarCache.has(avatarUrl)) {
      this.avatarImage = avatarCache.get(avatarUrl)!;
      return;
    }

    try {
      this.avatarImage = await loadAvatar(avatarUrl);
      avatarCache.set(avatarUrl, this.avatarImage);
    } catch (error) {
      console.error('Failed to load avatar:', error);
      this.avatarImage = null;
    }
  }

  /**
   * Initialize or resize offscreen canvas
   */
  private ensureOffscreenCanvas(config: SimulationConfig): void {
    // Calculate effective cell size to stay within browser canvas limits
    // Most browsers limit canvas to ~16384 pixels per dimension
    const MAX_CANVAS_SIZE = 16384;
    const maxDimension = Math.max(config.width, config.height);
    const maxCellSize = Math.floor(MAX_CANVAS_SIZE / maxDimension);
    const effectiveCellSize = Math.min(this.offscreenCellSize, maxCellSize);
    
    // Get actual dimensions for this grid type
    const dims = this.getOffscreenDimensions(config.width, config.height, effectiveCellSize);

    // Check if we need to create or resize
    if (
      !this.offscreenCanvas ||
      this.offscreenCanvas.width !== dims.width ||
      this.offscreenCanvas.height !== dims.height ||
      this.configId !== config.id
    ) {
      this.offscreenCanvas = document.createElement('canvas');
      this.offscreenCanvas.width = dims.width;
      this.offscreenCanvas.height = dims.height;
      this.offscreenCtx = this.offscreenCanvas.getContext('2d');
      this.lastRenderedState = null;
      this.configId = config.id;
      
      // Store the actual cell size used for this canvas
      (this as any)._actualOffscreenCellSize = effectiveCellSize;
    }
  }

  /**
   * Get the actual cell size used for the current offscreen canvas
   * (may be smaller than offscreenCellSize for large grids)
   */
  private getActualOffscreenCellSize(): number {
    return (this as any)._actualOffscreenCellSize || this.offscreenCellSize;
  }

  /**
   * Whether this renderer uses transparent background for proper tile overlap
   * Override in subclasses for non-square grids
   */
  protected usesTransparentBackground(): boolean {
    return false;
  }

  /**
   * Render grid to offscreen canvas (full redraw)
   * Optimized: only draws non-background cells
   */
  private renderGridToOffscreen(state: SimulationState, config: SimulationConfig): void {
    if (!this.offscreenCtx) return;

    const ctx = this.offscreenCtx;
    const cellSize = this.getActualOffscreenCellSize();

    // Clear canvas with background color (color 0)
    // This is much faster than drawing each cell individually
    if (this.usesTransparentBackground()) {
      ctx.clearRect(0, 0, this.offscreenCanvas!.width, this.offscreenCanvas!.height);
    } else {
      ctx.fillStyle = config.colors[0] || '#000000';
      ctx.fillRect(0, 0, this.offscreenCanvas!.width, this.offscreenCanvas!.height);
    }

    // Only draw cells that are NOT the background color (optimization for sparse grids)
    for (let y = 0; y < config.height; y++) {
      const row = state.grid[y];
      if (!row) continue;
      
      for (let x = 0; x < config.width; x++) {
        const colorIndex = row[x] ?? 0;
        // Skip background cells - they're already drawn by fillRect
        if (colorIndex === 0) continue;
        
        const color = config.colors[colorIndex] || config.colors[0];
        this.drawCell(ctx, x, y, color, cellSize);
      }
    }

    // Store state for incremental updates
    this.lastRenderedState = {
      grid: state.grid,
      stepCount: state.stepCount
    };
  }

  /**
   * Update only changed cells in offscreen canvas
   * Optimized: муравей меняет только одну клетку за шаг, 
   * поэтому при небольшом количестве шагов обновляем только изменённые клетки
   */
  private updateChangedCells(state: SimulationState, config: SimulationConfig): void {
    if (!this.offscreenCtx || !this.lastRenderedState) {
      this.renderGridToOffscreen(state, config);
      return;
    }

    const stepsDiff = state.stepCount - this.lastRenderedState.stepCount;
    
    // If too many steps passed, do full redraw (more efficient)
    if (stepsDiff > 1000 || stepsDiff < 0) {
      this.renderGridToOffscreen(state, config);
      return;
    }

    const ctx = this.offscreenCtx;
    const cellSize = this.getActualOffscreenCellSize();

    // For small step differences, scan for changed cells
    // This is still O(n²) but happens rarely due to local simulation
    const oldGrid = this.lastRenderedState.grid;
    for (let y = 0; y < config.height; y++) {
      const oldRow = oldGrid[y];
      const newRow = state.grid[y];
      if (!oldRow || !newRow) continue;
      
      for (let x = 0; x < config.width; x++) {
        const oldValue = oldRow[x] ?? 0;
        const newValue = newRow[x] ?? 0;

        if (oldValue !== newValue) {
          const color = config.colors[newValue] || config.colors[0];
          this.drawCell(ctx, x, y, color, cellSize);
        }
      }
    }

    // Update reference to current grid
    this.lastRenderedState = {
      grid: state.grid,
      stepCount: state.stepCount
    };
  }

  /**
   * Render the full simulation state with tiled rendering support
   * Uses offscreen canvas for efficiency
   * Requirements: 6.1, 6.2, 6.3, 6.4, 3.3, 3.4
   */
  render(state: SimulationState, config: SimulationConfig, options: RenderOptions): void {
    const { canvas, cellSize, offset, zoom } = options;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Ensure offscreen canvas exists
    this.ensureOffscreenCanvas(config);

    // Update offscreen canvas (incremental if possible)
    if (this.lastRenderedState && this.lastRenderedState.stepCount < state.stepCount) {
      this.updateChangedCells(state, config);
    } else if (!this.lastRenderedState) {
      this.renderGridToOffscreen(state, config);
    }

    // Calculate display parameters
    const effectiveCellSize = cellSize * zoom;
    const scale = effectiveCellSize / this.getActualOffscreenCellSize();
    
    // Get tile dimensions with separate cellGapX/cellGapY adjustments for proper tiling
    // cellGap values are multiplied by effectiveCellSize to make them proportional to cell size
    const cellGapXPixels = (config.cellGapX ?? 0) * effectiveCellSize;
    const cellGapYPixels = (config.cellGapY ?? 0) * effectiveCellSize;
    
    const tileDims = this.getTileDimensions(config.width, config.height, effectiveCellSize, cellGapXPixels, cellGapYPixels);
    const gridPixelWidth = tileDims.width;
    const gridPixelHeight = tileDims.height;

    // Clear canvas with background color
    ctx.fillStyle = config.colors[0] || '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Disable image smoothing for crisp pixels
    ctx.imageSmoothingEnabled = false;

    if (!this.offscreenCanvas) return;

    // Calculate visible tile range dynamically (infinite tiling)
    // Only render tiles that are actually visible on screen
    const minTileX = Math.floor(-offset.x / gridPixelWidth) - 1;
    const maxTileX = Math.ceil((canvas.width - offset.x) / gridPixelWidth);
    const minTileY = Math.floor(-offset.y / gridPixelHeight) - 1;
    const maxTileY = Math.ceil((canvas.height - offset.y) / gridPixelHeight);

    // Draw tiles by copying offscreen canvas with transformations
    for (let tileY = minTileY; tileY <= maxTileY; tileY++) {
      for (let tileX = minTileX; tileX <= maxTileX; tileX++) {
        const tileOffsetX = offset.x + tileX * gridPixelWidth;
        const tileOffsetY = offset.y + tileY * gridPixelHeight;

        // Skip tiles that are completely off-screen
        if (tileOffsetX + gridPixelWidth < 0 || tileOffsetX > canvas.width) continue;
        if (tileOffsetY + gridPixelHeight < 0 || tileOffsetY > canvas.height) continue;

        // Get tile transformation for topology (Req 3.4)
        const transform = getTileTransform(tileX, tileY, config.topology);

        ctx.save();
        ctx.translate(tileOffsetX, tileOffsetY);

        // Apply mirroring for non-orientable topologies
        if (transform.mirrorX || transform.mirrorY) {
          ctx.translate(
            transform.mirrorX ? gridPixelWidth : 0,
            transform.mirrorY ? gridPixelHeight : 0
          );
          ctx.scale(transform.mirrorX ? -1 : 1, transform.mirrorY ? -1 : 1);
        }

        // Scale and draw offscreen canvas
        ctx.scale(scale, scale);
        ctx.drawImage(this.offscreenCanvas, 0, 0);

        ctx.restore();

        // Draw ant in this tile
        this.drawAntInTile(ctx, state, config, tileOffsetX, tileOffsetY, effectiveCellSize, transform);
      }
    }
  }

  /**
   * Draw ant in a specific tile
   * Avatar image rotates inside the shape mask, but the mask itself stays fixed
   */
  protected drawAntInTile(
    ctx: CanvasRenderingContext2D,
    state: SimulationState,
    config: SimulationConfig,
    tileOffsetX: number,
    tileOffsetY: number,
    cellSize: number,
    transform: { mirrorX: boolean; mirrorY: boolean }
  ): void {
    const { width, height } = config;

    // Calculate ant position in this tile
    let antX = state.ant.position.x;
    let antY = state.ant.position.y;

    if (transform.mirrorX) antX = width - 1 - antX;
    if (transform.mirrorY) antY = height - 1 - antY;

    const cellPos = this.getCellPosition(antX, antY, cellSize);
    const screenX = tileOffsetX + cellPos.x + cellSize / 2;
    const screenY = tileOffsetY + cellPos.y + cellSize / 2;

    // Check if ant is visible
    if (screenX < -cellSize || screenX > ctx.canvas.width + cellSize) return;
    if (screenY < -cellSize || screenY > ctx.canvas.height + cellSize) return;

    // Calculate rotation angle
    let angle = this.getDirectionAngle(state.ant.direction);

    // Apply mirroring to angle
    if (transform.mirrorX) angle = Math.PI - angle;
    if (transform.mirrorY) angle = -angle;

    const avatarSize = cellSize * 0.8;

    ctx.save();
    ctx.translate(screenX, screenY);

    // Apply clipping for non-square grids (mask stays fixed, doesn't rotate)
    const clipPath = this.getAvatarClipPath(ctx, 0, 0, avatarSize);
    if (clipPath) {
      ctx.clip(clipPath);
    }

    // Now rotate only the image inside the mask
    ctx.rotate(angle);

    // Draw avatar image if loaded
    if (this.avatarImage) {
      ctx.drawImage(this.avatarImage, -avatarSize / 2, -avatarSize / 2, avatarSize, avatarSize);
    } else {
      // Fallback: red circle with direction indicator
      ctx.fillStyle = '#FF0000';
      ctx.beginPath();
      ctx.arc(0, 0, avatarSize / 2, 0, Math.PI * 2);
      ctx.fill();

      // Direction indicator
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -avatarSize / 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  /**
   * Render preview centered on ant
   * Requirements: 1.3
   * Optimized: renders only visible area around ant directly to canvas
   */
  renderPreview(state: SimulationState, config: SimulationConfig, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate cells to show based on canvas aspect ratio (3:2 for preview)
    const aspectRatio = canvas.width / canvas.height;
    const baseCells = 15;
    
    let previewCellsX: number;
    let previewCellsY: number;
    
    if (aspectRatio >= 1) {
      previewCellsY = baseCells;
      previewCellsX = Math.ceil(baseCells * aspectRatio);
    } else {
      previewCellsX = baseCells;
      previewCellsY = Math.ceil(baseCells / aspectRatio);
    }
    
    const cellSize = Math.min(canvas.width / previewCellsX, canvas.height / previewCellsY);
    
    // Clear with background color
    ctx.fillStyle = config.colors[0] || '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Calculate offset to center ant on screen
    const antX = state.ant.position.x;
    const antY = state.ant.position.y;
    
    // Get ant's pixel position using grid-specific method
    const antCellPos = this.getCellPosition(antX, antY, cellSize);
    
    // Offset to center ant on canvas
    const offsetX = canvas.width / 2 - antCellPos.x - cellSize / 2;
    const offsetY = canvas.height / 2 - antCellPos.y - cellSize / 2;
    
    // Calculate visible cell range (in virtual coordinates around ant)
    // For triangular grids, cells are half-width, so we need more cells horizontally
    const margin = 3;
    const halfCellsY = Math.ceil(previewCellsY / 2) + margin;
    // Double the X range for triangular grids (cells are half-width)
    const halfCellsX = Math.ceil(previewCellsX) + margin * 2;
    
    // Draw visible cells - iterate around ant position
    ctx.save();
    ctx.translate(offsetX, offsetY);
    
    for (let dy = -halfCellsY; dy <= halfCellsY; dy++) {
      for (let dx = -halfCellsX; dx <= halfCellsX; dx++) {
        // Virtual position (for drawing)
        const vx = antX + dx;
        const vy = antY + dy;
        
        // Wrapped position (for getting color from grid)
        const gx = ((vx % config.width) + config.width) % config.width;
        const gy = ((vy % config.height) + config.height) % config.height;
        
        const colorIndex = state.grid[gy]?.[gx] ?? 0;
        
        // Skip background cells (already filled)
        if (colorIndex === 0) continue;
        
        const color = config.colors[colorIndex] || config.colors[0];
        
        // Draw at virtual position (vx, vy) so geometry is correct
        this.drawCell(ctx, vx, vy, color, cellSize);
      }
    }
    
    ctx.restore();
    
    // Draw ant at center of canvas
    const avatarSize = cellSize * 0.8;
    const angle = this.getDirectionAngle(state.ant.direction);

    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    
    // Apply clipping for non-square grids
    const clipPath = this.getAvatarClipPath(ctx, 0, 0, avatarSize);
    if (clipPath) {
      ctx.clip(clipPath);
    }
    
    ctx.rotate(angle);

    if (this.avatarImage) {
      ctx.drawImage(this.avatarImage, -avatarSize / 2, -avatarSize / 2, avatarSize, avatarSize);
    } else {
      ctx.fillStyle = '#FF0000';
      ctx.beginPath();
      ctx.arc(0, 0, avatarSize / 2, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -avatarSize / 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  /**
   * Force full redraw of offscreen canvas
   * Call this when config changes significantly
   */
  invalidateCache(): void {
    this.lastRenderedState = null;
  }
}
